import { apiClient, unwrap } from '@/lib/apiClient';
import type {
  AllocationOptions,
  AllotmentPayload,
  SetClassTeacherPayload,
  TeacherAllocation,
} from '@/types/allocation';
import type { ApiSuccess } from '@/types/api';

/**
 * Every call the class-allotment screen makes.
 *
 * As with the rest of the institute area there is no institute id in any path — the
 * server takes it from the caller's token — and no academic year either: an
 * allotment always belongs to the institute's current session, and a year a client
 * could name would be a way to rewrite a closed one's teaching.
 */
export const allocationsApi = {
  /** This year's sections and the subjects each class teaches — the two pickers. */
  options: () =>
    unwrap(apiClient.get<ApiSuccess<AllocationOptions>>('/institute/allocations/options')),

  /** One teacher's classes, or the whole institute's when no uuid is given. */
  list: (staffUuid?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<TeacherAllocation[]>>('/institute/allocations', {
        params: staffUuid ? { staffUuid } : undefined,
      }),
    ),

  /**
   * Replaces this teacher's whole allotment for the year.
   *
   * A replace rather than add/remove calls because that is what the screen
   * does: the reader edits a list and saves it. Sending an empty array is
   * meaningful and clears their classes.
   */
  replace: (staffUuid: string, allotments: AllotmentPayload[]) =>
    unwrap(
      apiClient.put<ApiSuccess<TeacherAllocation[]>>(`/institute/allocations/staff/${staffUuid}`, {
        allotments,
      }),
    ),

  /**
   * Who runs one section, asked from the class's side.
   *
   * Both of these answer with the section's whole arrangement, so the screen that
   * called them can redraw from what was actually saved rather than from what it
   * hoped had been. The subject is only sent for a teacher who does not already
   * take one in the section — see `SetClassTeacherPayload`.
   */
  setClassTeacher: (classSectionUuid: string, payload: SetClassTeacherPayload) =>
    unwrap(
      apiClient.put<ApiSuccess<TeacherAllocation[]>>(
        `/institute/allocations/sections/${classSectionUuid}/class-teacher`,
        payload,
      ),
    ),

  /**
   * Who teaches one subject in one section — the class grid's cell. Null takes
   * it off whoever holds it; a class teacher keeps the register either way.
   */
  setSubjectTeacher: (classSectionUuid: string, subjectUuid: string, teacherUuid: string | null) =>
    unwrap(
      apiClient.put<ApiSuccess<{ teacher: { uuid: string; fullName: string } | null }>>(
        `/institute/allocations/sections/${classSectionUuid}/subjects/${subjectUuid}`,
        { teacherUuid },
      ),
    ),

  /** Leaves the section without a class teacher. Their subject is untouched. */
  clearClassTeacher: (classSectionUuid: string) =>
    unwrap(
      apiClient.delete<ApiSuccess<TeacherAllocation[]>>(
        `/institute/allocations/sections/${classSectionUuid}/class-teacher`,
      ),
    ),
};
